import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowLeft, ExternalLink, Database, Beaker, FlaskConical } from 'lucide-react'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Skeleton } from '../components/ui/Skeleton'
import { HelpTooltip } from '../components/ui/Tooltip'
import { InlineCopy } from '../components/ui/CopyButton'
import { useProteinSwissprotHits } from '../api/hooks'
import { getUniprotUrl } from '../utils/externalLinks'
import { getGlossaryEntry } from '../utils/glossary'

// Get Rhea URL from ID (handles RHEA:12345 format)
function getRheaUrl(rheaId: string): string {
  const numericId = rheaId.replace(/^RHEA:/i, '')
  return `https://www.rhea-db.org/rhea/${numericId}`
}

// Get ChEBI URL from ID
function getChebiUrl(chebiId: string): string {
  const numericId = chebiId.replace(/^CHEBI:/i, '')
  return `https://www.ebi.ac.uk/chebi/searchId.do?chebiId=${numericId}`
}

// Parse equation and render with ChEBI links
// Equation format: "compound1 + compound2 <=> compound3 + compound4"
// left_compounds and right_compounds contain the ChEBI IDs in order
function EquationWithLinks({
  equation,
  leftCompounds,
  rightCompounds,
}: {
  equation: string
  leftCompounds?: string[]
  rightCompounds?: string[]
}) {
  if (!equation) return <span className="text-gray-500">-</span>

  // Detect the separator (<=>, =>, <=, or =)
  let separator = ''
  let separatorDisplay = ''
  if (equation.includes('<=>')) {
    separator = '<=>'
    separatorDisplay = ' ⇌ '
  } else if (equation.includes('=>')) {
    separator = '=>'
    separatorDisplay = ' → '
  } else if (equation.includes('<=')) {
    separator = '<='
    separatorDisplay = ' ← '
  } else if (equation.includes('=')) {
    separator = '='
    separatorDisplay = ' = '
  } else {
    return <span className="text-gray-300">{equation}</span>
  }

  const [leftSide, rightSide] = equation.split(separator)
  const leftParts = leftSide.split('+').map((s) => s.trim())
  const rightParts = rightSide.split('+').map((s) => s.trim())

  const renderCompound = (name: string, chebiId?: string, key?: string) => {
    if (chebiId) {
      return (
        <a
          key={key}
          href={getChebiUrl(chebiId)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent-green hover:underline"
        >
          {name}
        </a>
      )
    }
    return <span key={key}>{name}</span>
  }

  return (
    <span className="text-gray-300">
      {leftParts.map((part, idx) => (
        <>
          {idx > 0 && <span className="text-gray-500"> + </span>}
          {renderCompound(part, leftCompounds?.[idx], `left-${idx}`)}
        </>
      ))}
      <span className="text-gray-500">{separatorDisplay}</span>
      {rightParts.map((part, idx) => (
        <>
          {idx > 0 && <span className="text-gray-500"> + </span>}
          {renderCompound(part, rightCompounds?.[idx], `right-${idx}`)}
        </>
      ))}
    </span>
  )
}

// Parse formula into JSX with subscripts
function FormulaDisplay({ formula }: { formula: string | undefined }) {
  if (!formula) return <span className="text-gray-500">-</span>

  const parts: React.ReactNode[] = []
  let i = 0
  let key = 0

  while (i < formula.length) {
    const char = formula[i]
    if (/\d/.test(char)) {
      let num = ''
      while (i < formula.length && /\d/.test(formula[i])) {
        num += formula[i]
        i++
      }
      parts.push(<sub key={key++}>{num}</sub>)
    } else {
      parts.push(<span key={key++}>{char}</span>)
      i++
    }
  }

  return <>{parts}</>
}

interface SwissprotHit {
  hit_num: number
  hsp_num: number
  swissprot_accession: string
  recname?: string
  evalue: number
  identity: number
  cover: number
  uniprot_url: string
  ec_numbers?: string[]
  kegg_ids?: string[]
  cofactors?: string[]
  cofactor_ids?: string[]
  reactions?: Reaction[]
}

interface Reaction {
  rhea_id: string
  equation?: string
  left_compounds?: string[]
  right_compounds?: string[]
}

interface ChebiCompound {
  chebi_id: string
  name?: string
  formula?: string
  smiles?: string
  pubchem_cid?: number
  count?: number
  best_evalue?: number
  structure_url?: string
  chebi_url?: string
}

interface SwissprotHitsResponse {
  hits: SwissprotHit[]
  unique_compounds?: ChebiCompound[]
}

export function SwissprotHitsPage() {
  const { clusterId, proteinId } = useParams<{ clusterId: string; proteinId: string }>()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'entries' | 'participants'>('entries')

  // Get domain-level filter params if present
  const resStartParam = searchParams.get('res_start')
  const resEndParam = searchParams.get('res_end')
  const rheaAccession = searchParams.get('rhea_accession') || undefined
  const resStart = resStartParam ? parseInt(resStartParam, 10) : undefined
  const resEnd = resEndParam ? parseInt(resEndParam, 10) : undefined
  const isDomainFiltered = resStart !== undefined && resEnd !== undefined
  const isRheaFiltered = !!rheaAccession

  const { data, isLoading, error } = useProteinSwissprotHits(
    proteinId || '',
    clusterId || '',
    resStart,
    resEnd,
    rheaAccession
  ) as {
    data: SwissprotHitsResponse | undefined
    isLoading: boolean
    error: Error | null
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-white mb-4">Failed to load SwissProt hits</h2>
        <Link to={`/proteins/${clusterId}/${proteinId}`}>
          <Button variant="secondary" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            Back to Protein
          </Button>
        </Link>
      </div>
    )
  }

  const hits = data.hits || []

  // Use unique compounds from API response (already sorted by best E-value)
  const allCompounds: ChebiCompound[] = data.unique_compounds || []

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Link
        to={`/proteins/${clusterId}/${proteinId}`}
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to {proteinId}
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-white">
          SwissProt BLAST Hits
          {isDomainFiltered && (
            <span className="text-lg font-normal text-gray-400 ml-3">
              (Domain {resStart}-{resEnd})
            </span>
          )}
        </h1>
        <p className="text-gray-400 mt-2">
          <span className="font-mono text-accent-cyan">{proteinId}</span>
          {' '}from cluster{' '}
          <Link to={`/clusters/${clusterId}`} className="font-mono text-accent-cyan hover:underline">
            {clusterId}
          </Link>
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <Badge variant="purple" size="md">
            {hits.length} SwissProt hits found
          </Badge>
          {isRheaFiltered && (
            <Badge variant="amber" size="md">
              <FlaskConical className="w-3 h-3 mr-1 inline" />
              Filtered by reaction: {rheaAccession}
            </Badge>
          )}
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        <button
          onClick={() => setActiveTab('entries')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'entries'
              ? 'bg-accent-purple/20 text-accent-purple border-b-2 border-accent-purple'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Database className="w-4 h-4 inline mr-2" />
          SwissProt Entries ({hits.length})
        </button>
        <button
          onClick={() => setActiveTab('participants')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'participants'
              ? 'bg-accent-green/20 text-accent-green border-b-2 border-accent-green'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Beaker className="w-4 h-4 inline mr-2" />
          Reaction participants ({allCompounds.length})
        </button>
      </div>

      {/* Content */}
      {activeTab === 'entries' ? (
        <Card>
          <CardContent className="pt-6">
            {hits.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-14">Hit</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-14">HSP</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">RecName</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-28">Accession</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">
                        <span className="inline-flex items-center gap-1">
                          E-value
                          <HelpTooltip content={getGlossaryEntry('evalue')?.definition || 'Expect value'} />
                        </span>
                      </th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">
                        <span className="inline-flex items-center gap-1">
                          Identity
                          <HelpTooltip content={getGlossaryEntry('identity')?.definition || 'Sequence identity'} />
                        </span>
                      </th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">
                        <span className="inline-flex items-center gap-1">
                          Cover
                          <HelpTooltip content={getGlossaryEntry('coverage')?.definition || 'Query coverage'} />
                        </span>
                      </th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-32">KEGG</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hits.map((hit, idx) => {
                      const hasReactions = hit.reactions && hit.reactions.length > 0
                      const numRows = hasReactions ? hit.reactions!.length + 1 : 1
                      const rowClass = idx % 2 === 0 ? 'bg-white/[0.02]' : ''

                      return (
                        <>
                          {/* Main row */}
                          <tr
                            key={`hit-${idx}`}
                            className={`border-b border-white/5 ${rowClass}`}
                          >
                            <td className="py-3 px-3 text-gray-500 align-top" rowSpan={numRows}>
                              {hit.hit_num}
                            </td>
                            <td className="py-3 px-3 text-gray-500 align-top" rowSpan={numRows}>
                              {hit.hsp_num}
                            </td>
                            <td className="py-3 px-3 text-gray-300" title={hit.recname}>
                              {hit.recname || '-'}
                            </td>
                            <td className="py-3 px-3">
                              <InlineCopy text={hit.swissprot_accession}>
                                <a
                                  href={getUniprotUrl(hit.swissprot_accession)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-mono text-accent-purple hover:underline inline-flex items-center gap-1"
                                >
                                  {hit.swissprot_accession}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </InlineCopy>
                            </td>
                            <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                              {hit.evalue.toExponential(2)}
                            </td>
                            <td className="py-3 px-3">
                              <Badge variant={hit.identity >= 90 ? 'green' : hit.identity >= 50 ? 'amber' : 'default'} size="sm">
                                {hit.identity.toFixed(1)}%
                              </Badge>
                            </td>
                            <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                              {hit.cover.toFixed(1)}%
                            </td>
                            <td className="py-3 px-3">
                              {hit.kegg_ids && hit.kegg_ids.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {hit.kegg_ids.map((keggId, kIdx) => (
                                    <a
                                      key={kIdx}
                                      href={`https://www.kegg.jp/entry/${keggId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="font-mono text-accent-cyan hover:underline text-xs"
                                    >
                                      {keggId}
                                    </a>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </td>
                          </tr>
                          {/* Reaction rows - inline like old layout with ChEBI links */}
                          {hasReactions && hit.reactions!.map((reaction, reactionIdx) => (
                            <tr
                              key={`reaction-${idx}-${reactionIdx}`}
                              className={`border-b border-white/5 ${rowClass}`}
                            >
                              <td colSpan={6} className="py-2 px-3">
                                <div className="text-sm">
                                  <EquationWithLinks
                                    equation={reaction.equation || ''}
                                    leftCompounds={reaction.left_compounds}
                                    rightCompounds={reaction.right_compounds}
                                  />
                                  {' '}
                                  <a
                                    href={getRheaUrl(reaction.rhea_id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-accent-amber hover:underline text-xs"
                                  >
                                    (RHEA:{reaction.rhea_id})
                                  </a>
                                </div>
                                {hit.ec_numbers && hit.ec_numbers[reactionIdx] && hit.ec_numbers[reactionIdx] !== '' && (
                                  <div className="text-gray-400 text-xs mt-1">
                                    EC number:{' '}
                                    <a
                                      href={`https://enzyme.expasy.org/EC/${hit.ec_numbers[reactionIdx]}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-accent-cyan hover:underline"
                                    >
                                      {hit.ec_numbers[reactionIdx]}
                                    </a>
                                  </div>
                                )}
                                {hit.cofactors && hit.cofactors.length > 0 && reactionIdx === 0 && (
                                  <div className="text-gray-400 text-xs mt-1">
                                    Cofactor:{' '}
                                    {hit.cofactors.map((cofactor, cofactorIdx) => {
                                      const cofactorId = hit.cofactor_ids?.[cofactorIdx]
                                      return (
                                        <span key={cofactorIdx}>
                                          {cofactorIdx > 0 && ', '}
                                          {cofactorId ? (
                                            <a
                                              href={`https://www.ebi.ac.uk/chebi/searchId.do?chebiId=${cofactorId}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-accent-cyan hover:underline"
                                            >
                                              {cofactor}
                                            </a>
                                          ) : (
                                            cofactor
                                          )}
                                        </span>
                                      )
                                    })}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No SwissProt hits found</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            {allCompounds.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-28">Ligand</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Number</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">Best E value</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Name</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-32">Formula</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">PubChem</th>
                      <th className="text-center py-2 px-3 text-gray-400 font-medium w-32">Structure</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allCompounds.map((compound, idx) => (
                      <>
                        {/* Main row */}
                        <tr
                          key={`compound-${idx}`}
                          className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}
                        >
                          <td className="py-3 px-3">
                            <a
                              href={`https://www.ebi.ac.uk/chebi/searchId.do?chebiId=${compound.chebi_id.replace('CHEBI:', '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-accent-green hover:underline inline-flex items-center gap-1 text-xs"
                            >
                              {compound.chebi_id}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="py-3 px-3 text-gray-300">
                            {compound.count || 1}
                          </td>
                          <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                            {compound.best_evalue != null ? compound.best_evalue.toExponential(2) : '-'}
                          </td>
                          <td className="py-3 px-3 text-gray-300 max-w-xs">
                            {compound.name ? (
                              <span
                                className="block truncate"
                                title={compound.name.replace(/<[^>]*>/g, '')}
                                dangerouslySetInnerHTML={{ __html: compound.name }}
                              />
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-gray-300 font-mono text-xs">
                            <FormulaDisplay formula={compound.formula} />
                          </td>
                          <td className="py-3 px-3">
                            {compound.pubchem_cid ? (
                              <a
                                href={`https://pubchem.ncbi.nlm.nih.gov/compound/${compound.pubchem_cid}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent-purple hover:underline text-xs"
                              >
                                {compound.pubchem_cid}
                              </a>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-center" rowSpan={2}>
                            <a
                              href={`https://www.rhea-db.org/chebi/${compound.chebi_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block"
                            >
                              <img
                                src={`https://www.rhea-db.org/chebi/${compound.chebi_id}`}
                                alt={compound.name || compound.chebi_id}
                                className="w-28 h-28 object-contain bg-white rounded mx-auto"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none'
                                }}
                              />
                            </a>
                          </td>
                        </tr>
                        {/* SMILES row */}
                        <tr
                          key={`smiles-${idx}`}
                          className={`border-b border-white/10 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}
                        >
                          <td colSpan={6} className="py-2 px-3">
                            {compound.smiles ? (
                              <div className="text-xs font-mono text-gray-500 break-all max-w-xl">
                                {compound.smiles}
                              </div>
                            ) : (
                              <span className="text-gray-500 text-xs">Not available</span>
                            )}
                          </td>
                        </tr>
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No reaction participants found</p>
            )}
          </CardContent>
        </Card>
      )}

    </div>
  )
}
